import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Euler, Mesh, Vector3 } from "three";
import { Model, useModelTemplate } from "../../../core/util/useModel";
import { addVectors, lookAt, makeTransform, vectorRight } from "../../../core/util/SpatialUtils";
import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
const POOL_GROUND_ANISO_LEVEL = 4;
const POOL_RIM_SCALE = 6;
export const WATER_CENTER = new Vector3(0, 0, 0.5);
export const Contact3DBackgroundObjects = () => {
    const three = useThree();
    const poolRimTemplate = useModelTemplate('models/poolRim.glb');
    const poolGroundTemplate = useModelTemplate('models/poolGround.glb', (model) => setTextureAniso(model, three.gl));
    const poolLadderTemplate = useModelTemplate('models/poolLadder.glb');
    const beachChairTemplate = useModelTemplate('models/beachChair.glb');
    const skyboxTemplate = useModelTemplate('models/skyBox.glb', (model) => model.traverse((object) => {
        if (object instanceof Mesh) {
            object.material.side = 0;
        }
    }));
    const poolRimTransform = makeTransform({
        scale: new Vector3(POOL_RIM_SCALE, POOL_RIM_SCALE, POOL_RIM_SCALE),
        position: addVectors(WATER_CENTER, new Vector3(0, -6, 0)),
    });
    const poolGroundTransform = makeTransform({
        scale: new Vector3(6, 6, 6),
        position: addVectors(WATER_CENTER, new Vector3(0, -0.25, 0)),
    });
    const ladderDirection = vectorRight();
    ladderDirection.applyEuler(new Euler(0, 5 * Math.PI / 6, 0));
    const ladderInitialTransform = makeTransform({
        position: addVectors(WATER_CENTER, ladderDirection.multiplyScalar(POOL_RIM_SCALE - 0.4)),
    });
    lookAt(ladderInitialTransform, WATER_CENTER);
    const beachChairTransforms = [
        makeTransform({
            scale: new Vector3(2, 2, 2),
            position: new Vector3(-8, 0, 0),
            rotation: new Euler(0, -Math.PI / 6, 0),
        }),
        makeTransform({
            scale: new Vector3(2, 2, 2),
            position: new Vector3(-8.5, 0, -5),
            rotation: new Euler(0, Math.PI / 6 + 0.15, 0),
        }),
        makeTransform({
            scale: new Vector3(2, 2, 2),
            position: new Vector3(7, 0, 2),
            rotation: new Euler(0, -4 * Math.PI / 6, 0),
        })
    ];
    const skyboxTransform = makeTransform({
        scale: new Vector3(15, 15, 15),
    });
    useEffect(() => {
        three.gl.shadowMap.needsUpdate = true;
    }, []);
    return _jsxs(_Fragment, { children: [_jsx(Model, { template: poolRimTemplate, initialTransform: poolRimTransform }), _jsx(Model, { template: poolGroundTemplate, initialTransform: poolGroundTransform, receiveShadow: true }), _jsx(Model, { template: poolLadderTemplate, initialTransform: ladderInitialTransform }), ...beachChairTransforms.map((transform) => _jsx(Model, { template: beachChairTemplate, castShadow: true, initialTransform: transform })), _jsx(Model, { template: skyboxTemplate, initialTransform: skyboxTransform })] });
};
function setTextureAniso(model, renderer) {
    if (model instanceof Mesh && model.material !== undefined) {
        const map = model.material.map;
        if (map !== null) {
            const maxAniso = renderer.capabilities.getMaxAnisotropy();
            const aniso = Math.min(maxAniso, POOL_GROUND_ANISO_LEVEL);
            map.anisotropy = aniso;
            const normalMap = model.material.normalMap;
            normalMap.anisotropy = aniso;
            normalMap.needsUpdate = true;
            model.material.needsUpdate = true;
            map.needsUpdate = true;
        }
    }
    for (const child of model.children) {
        setTextureAniso(child, renderer);
    }
}
//# sourceMappingURL=Contact3DBackgroundObjects.js.map